import { inject, Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from '../utils/auth.utils';
import { TabGetService } from '@techextensor/tab-core-utility';
import { UserRole } from '../const/constants';
import { HelperFunctionService } from './helper-function.service';
import { BroadcastChannelService } from './cross-tab-authentication.service';
import { TokenRefreshService } from './token-refresh.service';
import { OrgAppInfoHelperService } from './org-app-info-helper.service';
import { environment } from '../../../environments/environment';
import { TabSdk } from '@techextensor/tab-sdk';
import { TranslationToken } from '../const/translation.token';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public _authenticated: boolean = false;
    private _initializationComplete: boolean = false;
    public _tabGetService = inject(TabGetService);
    public _helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
    private _broadcastChannelService = inject(BroadcastChannelService);
    private _tokenRefreshService: TokenRefreshService = inject(TokenRefreshService);
    // private _sessionInactivityService: SessionInactivityService = inject(SessionInactivityService);
    private _orgAppInfoHelper: OrgAppInfoHelperService = inject(OrgAppInfoHelperService);
    // private readonly router: Router = inject(Router);

    set accessToken(token: string) {
        TabSdk.store.token = 'Bearer ' + token;
    }

    get accessToken(): string {
        return TabSdk.store.token ?? '';
    }


    constructor() {
        this._broadcastChannelService.authenticationEmitter.subscribe(isAuthenticated => {
            this._authenticated = isAuthenticated;
        })
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return from(TabSdk.auth.login(credentials)).pipe(
            switchMap(async (res: any) => {
                const authData = this.prepareAuthData(res);
                const { subdomain, domain } = this._orgAppInfoHelper.extractDomainInfo();
                const appCode = this.extractAppCodeFromPath();

                const userInfo = res?.Result?.user;
                const defaultAppId = userInfo?.DefaultAppId?.toLowerCase();
                const defaultTenantId = userInfo?.DefaultTenantId?.toLowerCase();
                const envTenantId = environment.commonConfig.tenantId?.toLowerCase();
                const envAppId = environment.commonConfig.applicationId?.toLowerCase();
                const envAppCode = environment.commonConfig.applicationCode;

                // Case 1: Main domain without subdomain
                if (this._orgAppInfoHelper.isValidDomain(domain) && !subdomain) {
                    return this.handleMainDomainLogin(authData, res, domain, defaultTenantId, defaultAppId, envTenantId, envAppId, envAppCode);
                }
                // Case 2: Subdomain without specific app
                else if (this._orgAppInfoHelper.isValidDomain(domain) && subdomain && !appCode) {
                    return this.handleSubdomainLogin(authData, res, subdomain, defaultAppId, envAppId, envTenantId);
                }
                // Case 3: Specific subdomain and specific app
                else {
                    return this.handleSpecificAppLogin(authData, res, domain, subdomain, appCode, envAppCode, envTenantId, envAppId);
                }
            })
        );
    }

    // Helper function to prepare auth data
    private prepareAuthData(res: any): any {
        return {
            userInfo: res.Result,
            token: `Bearer ${res.Result.Token}`,
            refreshToken: res?.Result?.RefreshToken,
            refreshTokenExpiry: res?.Result?.RefreshTokenExpiry,
        };
    }

    // Extract app code from path
    private extractAppCodeFromPath(): string | null {
        const match = document?.location?.pathname?.match(/^\/([^\/]+)/);
        return match?.[1] && match?.[1] !== 'sign-in' && match?.[1] !== 'sign-up' ? match[1] : null;
    }

    // Check if user has tenant admin role
    private hasTenantAdminRole(roles: any, envAppId: any): boolean {
        let isTenantAdmin = false;

        Object.keys(roles).forEach((key: any) => {
            const defaultAppRoles = roles[key]?.[envAppId]?.[environment?.id];
            defaultAppRoles?.forEach((role: any) => {
                if (role?.RoleName === UserRole.TenantAdministratorRoleName) {
                    isTenantAdmin = true;
                }
            });
        })
        return isTenantAdmin;
    }

    // Set window data and redirect
    private setWindowDataAndRedirect(
        authData: any, appCode: string, url: string, useRouter: boolean = false,
        queryParams: any = {}, preserveQueryParams: boolean = true
    ): any {
        if (useRouter) {
            url = this.buildUrlWithQueryParams(url, queryParams, preserveQueryParams);
        }

        window.location.replace(this._helperFunctionService.buildAuthTransferUrl(url, {
            authData,
            appCode: appCode || environment.commonConfig.applicationCode
        }));
        return of(null);
    }

    // Helper method to build URL with query parameters
    private buildUrlWithQueryParams(baseUrl: string, newQueryParams: any = {}, preserveQueryParams: boolean = true): string {
        const currentUrl = new URL(window.location.href);
        const url = new URL(baseUrl, window.location.origin);

        // If preserveQueryParams is true, start with existing query parameters
        if (preserveQueryParams) {
            currentUrl.searchParams.forEach((value, key) => {
                url.searchParams.set(key, value);
            });
        }

        // Add/override with new query parameters
        Object.keys(newQueryParams).forEach(key => {
            if (newQueryParams[key] !== undefined && newQueryParams[key] !== null) {
                url.searchParams.set(key, newQueryParams[key]);
            }
        });

        return url.toString();
    }

    // Handle unauthorized access
    private handleUnauthorizedAccess(appCode: string): any {
        TabSdk.ui.showError({
            title: TranslationToken.AuthorizationFailed,
            message: TranslationToken.UnauthorizedToAccessApp,
        });
        return of(null);
    }

    // Main domain login handler
    private async handleMainDomainLogin(
        authData: any, res: any, domain: any, defaultTenantId: string,
        defaultAppId: string, envTenantId: string, envAppId: string, envAppCode: string
    ): Promise<any> {
        let url = 'https://';
        let defaultAppCode;

        // Default tenant matches environment tenant
        if (defaultTenantId === envTenantId) {
            defaultAppCode = envAppCode;
            url += `${environment.commonConfig.tenantName}.${domain}/initializing`;
        }
        // Default app matches environment app
        else if (defaultAppId === envAppId) {
            if (defaultTenantId !== envTenantId) {
                if (!this.hasTenantAdminRole(res?.Result?.user?.Roles, envAppId)) {
                    return this.handleUnauthorizedAccess(envAppCode);
                } else {
                    const orgInfoRes: any = await firstValueFrom(this._orgAppInfoHelper.getOrgInfoApiObservable({ Id: defaultTenantId }));
                    const defaultSubdomain = orgInfoRes?.Result?.[0]?.Subdomain;
                    defaultAppCode = envAppCode;
                    url += `${defaultSubdomain}.${domain}/initializing?redirectURL=admin`;
                }
            } else {
                defaultAppCode = envAppCode;
                url += `${environment.commonConfig.tenantName}.${domain}/initializing`;
            }
        }
        // Neither matches environment defaults
        else {
            const orgInfoRes: any = await firstValueFrom(this._orgAppInfoHelper.getOrgInfoApiObservable({ Id: defaultTenantId }));
            const appDetailResponse: any = await firstValueFrom(this._orgAppInfoHelper.getAppInfoApiObservable({ AppId: defaultAppId }));
            defaultAppCode = appDetailResponse?.Result?.[0]?.AppName;
            url += `${orgInfoRes?.Result?.[0]?.Subdomain}.${domain}/initializing`;
        }

        return this.setWindowDataAndRedirect(authData, defaultAppCode, url);
    }

    // Subdomain login handler
    private async handleSubdomainLogin(
        authData: any, res: any, subdomain: any, defaultAppId: string,
        envAppId: string, envTenantId: string
    ): Promise<any> {
        let url = 'initializing';
        let defaultAppCode;
        let queryParams = {};
        let preserveQueryParams = true;

        // Default app matches environment app
        if (defaultAppId === envAppId) {
            if (subdomain !== environment.commonConfig.tenantName) {
                if (!this.hasTenantAdminRole(res?.Result?.user?.Roles, envAppId)) {
                    return this.handleUnauthorizedAccess(environment.commonConfig.applicationCode);
                } else {
                    defaultAppCode = environment.commonConfig.applicationCode;
                    queryParams = { redirectURL: 'admin' };
                    preserveQueryParams = false;
                }
            } else {
                defaultAppCode = environment.commonConfig.applicationCode;
            }
        }
        // Default app doesn't match environment app
        else {
            const appDetailResponse: any = await firstValueFrom(this._orgAppInfoHelper.getAppInfoApiObservable({ AppId: defaultAppId }));
            const defaultAppDetail = appDetailResponse?.Result?.[0];
            this._orgAppInfoHelper.setAppInfo(defaultAppDetail);
            defaultAppCode = defaultAppDetail.AppName;
        }

        return this.setWindowDataAndRedirect(authData, defaultAppCode, url, true, queryParams, preserveQueryParams);
    }

    // Specific app login handler
    private handleSpecificAppLogin(
        authData: any, res: any, domain: any, subdomain: any, appCode: any,
        envAppCode: string, envTenantId: string, envAppId: string
    ): Promise<any> {
        let url = 'initializing';
        let queryParams = {};
        let preserveQueryParams = true;

        // App code matches environment app code
        if (appCode === envAppCode && this._orgAppInfoHelper.isValidDomain(domain)) {
            if (subdomain !== environment.commonConfig.tenantName) {
                if (!this.hasTenantAdminRole(res?.Result?.user?.Roles, envAppId)) {
                    return this.handleUnauthorizedAccess(envAppCode);
                } else {
                    queryParams = { redirectURL: 'admin' };
                    preserveQueryParams = false;
                }
            }
        }

        return this.setWindowDataAndRedirect(authData, appCode, url, true, queryParams, preserveQueryParams);
    }

    /**
     * Sign up a new tenant user
     *
     * @param credentials - The user's credentials including first name, last name, email, password, tenant name, and terms
     * @returns An Observable that emits the response from the server
     */
    signUp(credentials: {
        AdminFirstName: string, AdminLastName: string, AdminEmail: string,
        Password: string, TenantName: string, terms: boolean
    }): Observable<any> {

        return from(TabSdk.auth.registerTenant(credentials)).pipe(
            switchMap(async (res: any) => {
                
                TabSdk.store.tenantId = res?.user?.DefaultTenantId;
                TabSdk.store.refreshToken = res?.RefreshToken;
                TabSdk.store.setUserInfo(res);
                
                this.accessToken = res?.Token;
                this._authenticated = true;

                return of(res);
            })
        );
    }

    signInUsingToken(): Observable<any> {
        if (AuthUtils._verifyJWTToken(this.accessToken)) {
            this._authenticated = true;
            return of(true);
        }
        else {
            return of(false);
        }
    }

    /**
     * Sign out the user by removing the user info from local storage and the access token from session storage.
     * Also, clear the IndexedDB.
     *
     * @return {Observable<any>} A observable that emits true when the user is successfully signed out.
     */
    signOut(backtrace: boolean = true): Observable<any> {

        this._broadcastChannelService.broadcastSignOut(backtrace);
        this._tokenRefreshService.resetServiceState();

        // Return an observable that emits true
        return of(true);
    }

    check(): Observable<boolean> {
        if (!this._initializationComplete) {
            return new Observable((observer) => {
                this._broadcastChannelService.initialize().then((result: any) => {
                    this._initializationComplete = true;
                    this._authenticated = result;
                    observer.next(result);
                })
            });
        }

        if (this._authenticated) {
            TabSdk.store.removeStaticToken();
            return of(true);
        }


        if (!this.accessToken) {
            TabSdk.store.staticToken = environment.commonConfig.staticToken;
            return of(false);
        }

        if(!this._authenticated){
            TabSdk.store.removeToken();
            TabSdk.store.staticToken = environment.commonConfig.staticToken;
            return of(false);
        }

        return this.signInUsingToken();
    }
}
